import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Formik, FormikHelpers } from 'formik';
import { FormikProps } from 'formik/dist/types';
import { DateTime } from 'luxon';
import { ReactElement, SyntheticEvent, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { getSiteByUuid, getSiteDetailsById, setSiteJob } from '../../redux/slices/sites/sitesSlice';
import { useDispatch } from '../../redux/store';
import { TextField } from '../../styled/inputs';
import { DatePicker } from '../datePicker/DatePicker';

const JOB_NUMBER_MAX = 64;
const DESCRIPTION_MAX = 512;

interface IProp {
  open: boolean;
  siteId?: string;
  onClose: () => void;
}

interface Values {
  jobNumber: string;
  description: string;
  activeFrom: Date | null;
  activeTo: Date | null;
}

const schema = yup.object().shape({
  jobNumber: yup.string().trim().max(JOB_NUMBER_MAX, `Should be ${JOB_NUMBER_MAX} characters or fewer`),
  description: yup.string().max(DESCRIPTION_MAX, `Should be ${DESCRIPTION_MAX} characters or fewer`),
});

const toISO = (value: Date | null): string | undefined => (value ? DateTime.fromJSDate(value).toUTC().toISO() ?? undefined : undefined);
const toDate = (value?: string | null): Date | null => (value ? DateTime.fromISO(value).toJSDate() : null);

// Editor for a job site's client job number. Creating and editing are the same
// form: a blank site creates the job on save, a populated one edits it in place.
// Clearing the number unlinks the job from the site.
export const SiteJobForm = ({ open, siteId, onClose }: IProp): ReactElement => {
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<Values>>(null);
  const site = useSelector(getSiteDetailsById(siteId));

  const handleClose = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>, reason?: string) => {
      if (reason !== 'backdropClick') {
        onClose();
      }
    },
    [onClose],
  );

  const submit = async (values: Values, { setSubmitting, setFieldError }: FormikHelpers<Values>) => {
    if (!site?.id) return;
    try {
      setSubmitting(true);
      await dispatch(
        setSiteJob({
          siteId: site.id,
          body: {
            // An empty number is the documented way to clear the link.
            job_number: values.jobNumber.trim(),
            description: values.description.trim() || undefined,
            active_from: toISO(values.activeFrom),
            active_to: toISO(values.activeTo),
          },
        }),
      ).unwrap();
      await dispatch(getSiteByUuid(site.id));
      onClose();
    } catch (error) {
      // A duplicate number is the one error a user can act on, so surface it on
      // the field rather than only as a toast.
      const status = (error as { code?: number })?.code;
      if (status === 409) {
        setFieldError('jobNumber', 'That job number is already used by another job site');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}>
      <Formik
        innerRef={formikRef}
        validationSchema={schema}
        onSubmit={submit}
        enableReinitialize
        initialValues={{
          jobNumber: site?.job_number || '',
          description: site?.job_description || '',
          // A new job starts when its job site did; an existing job keeps its own date.
          activeFrom: site?.job_id ? toDate(site?.job_active_from) : toDate(site?.created_at),
          activeTo: toDate(site?.job_active_to),
        }}
      >
        {({ handleSubmit, handleChange, handleBlur, setFieldValue, values, touched, errors, isSubmitting }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h3" component="div">
                  Job #
                </Typography>
                <IconButton onClick={handleClose}>
                  <CloseOutlinedIcon />
                </IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <TextField
                name="jobNumber"
                label="Job #"
                value={values.jobNumber}
                error={Boolean(touched.jobNumber && errors.jobNumber)}
                helperText={(touched.jobNumber && errors.jobNumber) || 'Clear this field to remove the job number from this site'}
                onBlur={handleBlur}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                autoFocus
                my={2}
                inputProps={{ maxLength: JOB_NUMBER_MAX }}
              />
              <TextField
                name="description"
                label="Description"
                value={values.description}
                error={Boolean(touched.description && errors.description)}
                helperText={touched.description && errors.description}
                onBlur={handleBlur}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                my={2}
                inputProps={{ maxLength: DESCRIPTION_MAX }}
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} sx={{ mt: 2 }}>
                  <DatePicker
                    label="From"
                    withClear
                    value={values.activeFrom}
                    onChange={(value) => setFieldValue('activeFrom', value || null)}
                    InputProps={{ fullWidth: true }}
                  />
                  <DatePicker
                    label="To"
                    withClear
                    value={values.activeTo}
                    onChange={(value) => setFieldValue('activeTo', value || null)}
                    helperText="Leave blank while the job is active"
                    InputProps={{ fullWidth: true }}
                  />
                </Stack>
              </LocalizationProvider>
            </DialogContent>
            <DialogActions sx={{ px: 6, pb: 6 }}>
              <Button onClick={handleClose} color="primary" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};
